const TONE_KEYS = {
  professional: {
    labelKey: "replies.professional",
    placeholderDe:
      "Vielen Dank für Ihr Feedback. Wir nehmen Ihre Anliegen ernst und möchten das gerne weiter besprechen…",
    placeholderEn:
      "Thank you for your feedback. We take your concerns seriously and would like to discuss this further…",
    thanksDe:
      "Vielen Dank für Ihre freundliche Bewertung. Wir freuen uns über Ihr Feedback und hoffen, Sie bald wieder begrüßen zu dürfen.",
    thanksEn:
      "Thank you for your kind review. We appreciate your feedback and look forward to serving you again.",
  },
  friendly: {
    labelKey: "replies.friendly",
    placeholderDe:
      "Hallo! Vielen Dank, dass Sie Ihre Erfahrung mit uns geteilt haben — wir würden das gerne klären…",
    placeholderEn:
      "Hi there! Thanks so much for sharing your experience with us — we'd love to make things right…",
    thanksDe:
      "Danke für die tolle Bewertung! Wir freuen uns, dass Sie eine großartige Erfahrung mit uns hatten.",
    thanksEn:
      "Thanks a million for the awesome review! We're thrilled you had a great experience with us.",
  },
};

const TEMPLATE_DEFS = [
  { id: "apology-service", labelKey: "replies.serviceApology", tone: "professional" },
  { id: "apology-wait", labelKey: "replies.waitTime", tone: "professional" },
  { id: "thanks-positive", labelKey: "replies.thankPositive", tone: "friendly" },
  { id: "invite-offline", labelKey: "replies.inviteOffline", tone: "professional" },
  { id: "billing-clarify", labelKey: "replies.billingClarify", tone: "professional" },
];

const TEMPLATE_TEXT = {
  "apology-service": {
    de: "Vielen Dank, dass Sie uns darauf aufmerksam gemacht haben. Wir entschuldigen uns aufrichtig für die beschriebene Erfahrung. Bitte kontaktieren Sie uns direkt, damit wir die Angelegenheit umgehend klären können.",
    en: "Thank you for bringing this to our attention. We sincerely apologize for the experience you described. Please contact us directly so we can resolve this matter promptly.",
  },
  "apology-wait": {
    de: "Wir entschuldigen uns für die Wartezeit. Das entspricht nicht unserem Standard, und wir überprüfen unsere Abläufe, um die Reaktionszeiten zu verbessern.",
    en: "We apologize for the wait time you experienced. This is not the standard we aim for, and we are reviewing our processes to improve response times.",
  },
  "thanks-positive": {
    de: "Vielen Dank für die wunderbare Bewertung! Wir freuen uns, dass Sie eine großartige Erfahrung hatten und heißen Sie gerne wieder willkommen.",
    en: "Thank you so much for the wonderful review! We're delighted you had a great experience and look forward to welcoming you again.",
  },
  "invite-offline": {
    de: "Wir möchten mehr über Ihre Erfahrung erfahren. Bitte wenden Sie sich direkt an unser Team, damit wir Ihre Anliegen persönlich klären können.",
    en: "We would like to learn more about your experience. Please reach out to our team directly so we can address your concerns personally.",
  },
  "billing-clarify": {
    de: "Vielen Dank für Ihr Feedback zur Abrechnung. Wir prüfen Ihre Rechnung gerne und klären alle Posten. Bitte kontaktieren Sie unser Abrechnungsteam.",
    en: "Thank you for your feedback regarding billing. We'd be happy to review your invoice and clarify any charges. Please contact our billing team.",
  },
};

export function getReplyTones(t, locale) {
  const lang = locale === "en" ? "en" : "de";
  return Object.fromEntries(
    Object.entries(TONE_KEYS).map(([key, def]) => [
      key,
      {
        label: t(def.labelKey),
        placeholder: lang === "de" ? def.placeholderDe : def.placeholderEn,
        quickThanks: lang === "de" ? def.thanksDe : def.thanksEn,
      },
    ])
  );
}

export function getReplyTemplateLibrary(t, locale) {
  const lang = locale === "en" ? "en" : "de";
  return TEMPLATE_DEFS.map((def) => ({
    id: def.id,
    label: t(def.labelKey),
    tone: def.tone,
    text: TEMPLATE_TEXT[def.id][lang],
  }));
}

export function getReplyTemplate(tone, review, t, locale) {
  const tones = getReplyTones(t, locale);
  const author = review?.author?.split(" ")[0] || (locale === "en" ? "there" : "Sie");
  const templates = tones[tone] || tones.professional;

  if (Number(review?.rating) >= 4) {
    return templates.quickThanks.replace(/there|Sie/g, author);
  }

  return templates.placeholder.replace(/there|Sie/g, author);
}

export function getTemplatesForTone(tone, t, locale) {
  return getReplyTemplateLibrary(t, locale).filter((item) => item.tone === tone);
}

export function generateAiReplySuggestion(review, t, locale, tone = "professional") {
  const rating = Number(review?.rating) || 0;
  const text = (review?.text || "").toLowerCase();
  const author = review?.author?.split(" ")[0] || (locale === "en" ? "there" : "Sie");
  const library = getReplyTemplateLibrary(t, locale);

  if (rating >= 4) {
    return getReplyTemplate("friendly", review, t, locale);
  }

  if (/wait|slow|delay|hour|minute|warte|stunde|minute/.test(text)) {
    const tpl = library.find((item) => item.id === "apology-wait")?.text;
    return tpl?.replace(/^We /, `Hi ${author}, we `) || tpl;
  }

  if (/bill|invoice|charge|price|cost|rechnung|kosten|preis/.test(text)) {
    return library.find((item) => item.id === "billing-clarify")?.text;
  }

  if (/rude|staff|support|service|never|unfreund|personal|nie/.test(text)) {
    return library.find((item) => item.id === "apology-service")?.text;
  }

  return getReplyTemplate(tone, review, t, locale);
}
